import React, {
    ReactNode,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState
} from 'react';
import {createPortal} from 'react-dom';
import classNames from 'classnames';
import {XIcon} from 'lucide-react';
import Icon from '../shared/Icon.tsx';
import {SidePanelContext, useSidePanelContext} from './sidePanelContext.ts';

// Canvases (Fabric, Konva, three) size themselves to their container, so
// tell them the layout changed once the panel has taken or given up its
// space. useFocusMode does the same.
function announceResize() {
    window.requestAnimationFrame(() =>
        window.dispatchEvent(new Event('resize'))
    );
}

export const SidePanelProvider: React.FC<{children: ReactNode}> = ({
    children
}) => {
    const [openId, setOpenId] = useState<string | null>(null);
    const [outlet, setOutlet] = useState<HTMLElement | null>(null);
    const opener = useRef<HTMLElement | null>(null);

    const open = useCallback((id: string) => {
        opener.current = document.activeElement as HTMLElement | null;
        setOpenId(id);
        announceResize();
    }, []);

    const close = useCallback(() => {
        setOpenId(null);
        announceResize();
        opener.current?.focus?.();
        opener.current = null;
    }, []);

    const value = useMemo(
        () => ({openId, open, close, outlet, setOutlet}),
        [openId, open, close, outlet]
    );

    return (
        <SidePanelContext.Provider value={value}>
            {children}
        </SidePanelContext.Provider>
    );
};

/*
 * Where docked panels render: the last child of a flex row beside the
 * work. Without one on the page, panels fall back to a sheet over the
 * body.
 */
export const SidePanelOutlet: React.FC<{className?: string}> = ({
    className
}) => {
    const {setOutlet} = useSidePanelContext();
    return (
        <div
            className={classNames('mx-side-panel-outlet', className)}
            ref={setOutlet}
        />
    );
};

interface SidePanelProps {
    id: string;
    title: ReactNode;
    children: ReactNode;
    // A line at the bottom: "Changes save as you go."
    footer?: ReactNode;
    className?: string;
}

/*
 * A 400px panel for anything you do while looking at your work: share,
 * details, feedback, properties. Docked on a laptop, a sheet with a scrim on
 * a tablet or phone. Esc closes it and focus returns to what opened it.
 */
const SidePanel: React.FC<SidePanelProps> = ({
    id,
    title,
    children,
    footer,
    className
}) => {
    const {openId, close, outlet} = useSidePanelContext();
    const titleId = useId();
    const closeButton = useRef<HTMLButtonElement>(null);
    const isOpen = openId === id;

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        closeButton.current?.focus();

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, close]);

    if (!isOpen) {
        return null;
    }

    const panel = (
        <>
            <div className="mx-side-panel-scrim" onClick={close} />
            <aside
                className={classNames('mx-side-panel', className)}
                role="dialog"
                aria-modal="false"
                aria-labelledby={titleId}
            >
                <div className="mx-side-panel-header">
                    <h2 id={titleId} className="mx-side-panel-title">
                        {title}
                    </h2>
                    <button
                        ref={closeButton}
                        type="button"
                        className="btn btn-tertiary btn-icon-only"
                        aria-label="Close panel"
                        onClick={close}
                    >
                        <Icon icon={XIcon} size="s" />
                    </button>
                </div>
                <div className="mx-side-panel-body">{children}</div>
                {footer && <div className="mx-side-panel-footer">{footer}</div>}
            </aside>
        </>
    );

    return createPortal(
        outlet ? panel : <div className="mx-side-panel-sheet">{panel}</div>,
        outlet ?? document.body
    );
};

export default SidePanel;
